/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * CollapsibleEditorSection (guidance §17) — the shared disclosure that turns a
 * long authoring step into a scannable list. Generalizes the section collapse the
 * ORTB body editor grew internally and the detail page's local `CollapsibleSection`.
 *
 * The header always advertises whether the section needs attention even when
 * collapsed: a **count** badge and the **worst-severity dot** ({@link IssueDot},
 * from the shared warnings model). An optional `actions` slot holds right-aligned
 * controls (Add / Edit / Save·Cancel) so the editor body stays chrome-free.
 */

import React from 'react';
import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import { IssueDot } from './ValidationSummary';
import type { MappingIssueLevel } from './mapping-issues';

export interface CollapsibleEditorSectionProps {
  title: string;
  /** Optional one-line hint under the title. */
  description?: string;
  /** Item count shown as a muted badge, e.g. "3". */
  count?: number;
  /** Worst-severity dot for the section (from the section's issues). */
  status?: MappingIssueLevel | null;
  /** Right-aligned controls (Add / Edit / Save·Cancel). Clicks don't toggle. */
  actions?: React.ReactNode;
  defaultOpen?: boolean;
  /** Controlled open state (optional — omit for self-managed). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export const CollapsibleEditorSection: React.FC<CollapsibleEditorSectionProps> = ({
  title,
  description,
  count,
  status,
  actions,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  children,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;

  const toggle = () => {
    const next = !open;
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Box borderWidth="1px" borderColor="border.default" borderRadius="8px" overflow="hidden" bg="bg.card">
      <Flex
        align="center"
        justify="space-between"
        gap={2}
        px={4}
        py={2.5}
        bg="bg.subtle"
        cursor="pointer"
        userSelect="none"
        onClick={toggle}
        _hover={{ bg: 'bg.canvas' }}
      >
        <HStack gap={2} align="center" minW={0}>
          <Text fontSize="xs" color="fg.muted" fontWeight="700" flexShrink={0}>
            {open ? '▾' : '▸'}
          </Text>
          <Text fontSize="sm" fontWeight="600" color="fg.default" truncate>
            {title}
          </Text>
          {typeof count === 'number' && (
            <Box
              as="span"
              flexShrink={0}
              bg="bg.canvas"
              color="fg.muted"
              borderRadius="full"
              px={2}
              fontSize="xs"
              fontWeight="600"
            >
              {count}
            </Box>
          )}
          <IssueDot level={status ?? null} title={status ? `${status} in ${title}` : undefined} />
        </HStack>
        {actions && (
          <HStack gap={2} flexShrink={0} onClick={(e) => e.stopPropagation()}>
            {actions}
          </HStack>
        )}
      </Flex>
      {open && (
        <Box px={4} py={3}>
          {description && (
            <Text fontSize="xs" color="fg.muted" mb={3}>
              {description}
            </Text>
          )}
          {children}
        </Box>
      )}
    </Box>
  );
};
